/** MatchLink backed by the offline worker (sim + filter + bots off the main thread). */
import { intentMessage } from '@sa/protocol';
import type { Intent } from '@sa/sim';
import type { MatchEvents, MatchLink } from '../game/link';
import type { FromWorker, OfflineStart, ToWorker } from './protocol';

export class OfflineLink implements MatchLink {
  readonly local = true;
  readonly tickMs: number;
  private worker: Worker;
  private h: MatchEvents | null = null;
  private queue: FromWorker[] = [];
  private seq = 1;

  constructor(start: Omit<OfflineStart, 'type'>) {
    this.tickMs = 100 / start.speed;
    this.worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent<FromWorker>) => (this.h ? this.dispatch(e.data) : this.queue.push(e.data));
    this.worker.onerror = (e) => this.h?.error(e.message);
    this.post({ type: 'start', ...start }, [start.world.bin, start.world.coast]);
  }

  private post(m: ToWorker, transfer: Transferable[] = []): void {
    this.worker.postMessage(m, transfer);
  }

  private dispatch(m: FromWorker): void {
    const h = this.h!;
    switch (m.type) {
      case 'welcome':
        h.welcome(m.welcome);
        break;
      case 'snap':
        h.snapshot(m.buf);
        break;
      case 'ack':
        h.ack(m.seq, m.ok, m.err);
        break;
      case 'end':
        h.end(m.end);
        break;
      case 'error':
        h.error(m.msg);
        break;
      case 'devReady':
        // dev screenshots: look at where it happened, then let the shot script proceed
        if (m.x !== undefined) window.dispatchEvent(new CustomEvent('sa:devlook', { detail: { x: m.x, y: m.y } }));
        (window as unknown as { __saUntil: boolean }).__saUntil = true;
        break;
    }
  }

  bind(h: MatchEvents): void {
    this.h = h;
    const q = this.queue;
    this.queue = [];
    q.forEach((m) => this.dispatch(m));
  }

  send(intent: Intent): number {
    const seq = this.seq++;
    this.post({ type: 'intent', msg: intentMessage(intent, seq) });
    return seq;
  }

  ping(): void {}

  pause(paused: boolean): void {
    this.post({ type: 'pause', paused });
  }

  close(): void {
    this.worker.terminate();
  }
}
